import React from 'react'
import { Box, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMinus, faPlus, faTextSize } from '@fortawesome/pro-solid-svg-icons'
import { FontSizeControlOptions } from './types'
import { ControlsGroup, ControlWrapper, Icon, StyledIconButton, TextWrapper, ValueDisplay } from './styles'

const PERCENTAGE_ID = 'current-font-size'

interface Props {
  className?: string
  style?: React.CSSProperties
  id?: string
  value: number
  options?: FontSizeControlOptions
  onDecrease: () => void
  onIncrease: () => void
  onReset?: () => void
  isDefaultFontSize: boolean
  isMobile?: boolean
}

const TextResize: React.FC<Props> = ({
  value,
  onDecrease,
  onIncrease,
  onReset,
  isMobile,
  isDefaultFontSize,
}) => {
  const calculatePercentage = Math.round((value / 16) * 100)

  return (
    <ControlWrapper role='group' aria-label='Text Size controls' isMobile={isMobile}>
      <TextWrapper>
        {isMobile && <Icon icon={faTextSize} />}
        {/* a11y: hidden so the "Text Size controls" group name isn't announced twice */}
        <Typography variant='body2' aria-hidden>
          Text Size
        </Typography>
      </TextWrapper>
      <ControlsGroup>
        <StyledIconButton
          onClick={onDecrease}
          aria-label='Decrease Font Size'
          aria-describedby={PERCENTAGE_ID}
          data-automation-id='decrease-font-size'
        >
          <FontAwesomeIcon icon={faMinus} aria-hidden='true' />
        </StyledIconButton>
        <ValueDisplay
          onClick={onReset}
          aria-label={`Reset text size to default. Current font size is ${calculatePercentage}%.`}
          data-automation-id='font-size-display'
          {...(isDefaultFontSize ? { tabIndex: -1 } : { role: 'button', tabIndex: 0 })}
        >
          <Box id={PERCENTAGE_ID} role='status' aria-live='polite'>
            {calculatePercentage}%
          </Box>
        </ValueDisplay>
        <StyledIconButton
          onClick={onIncrease}
          aria-label='Increase Font Size'
          aria-describedby={PERCENTAGE_ID}
          data-automation-id='increase-font-size'
        >
          <FontAwesomeIcon icon={faPlus} aria-hidden='true' />
        </StyledIconButton>
      </ControlsGroup>
    </ControlWrapper>
  )
}

export default TextResize
